/**
 * Payout list.
 *
 * WHY declared / computed / delta sit side by side: the whole product is R1 — the platform's
 * declared net versus the net we recomputed from the lines. Showing only one of them would hide
 * the answer the seller came for.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Amount } from "../components/Amount";
import { ResultBadge, StatusBadge } from "../components/Badges";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { formatCount } from "../money";
import { formatDateTime, shortExternalId } from "../format";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

const PAGE_SIZE = 50;

export function Payouts(): ReactNode {
  const store = useStore();
  const state = useAsync(() => api.payouts(store.id, { limit: PAGE_SIZE }), [store.id]);

  if (state.status === "loading") return <Loading label="Loading payouts" />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={state.reload} />;
  const { payouts } = state.data;

  return (
    <>
      <h1 className="page-title">Payouts</h1>
      {payouts.length === 0 ? (
        <EmptyState
          title="No payouts yet"
          hint="A payout appears here once the platform closes one and our sync picks it up. Everything earned before that is under Unsettled."
          action={<Link className="btn" to={`/stores/${store.id}/unsettled`}>See unsettled lines</Link>}
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Issued</th><th>Payout</th><th>Status</th>
                <th className="num">Declared net</th><th className="num">Computed net</th><th className="num">Delta</th>
                <th>Result</th><th className="num">Lines</th><th className="num">Open findings</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="row-link">
                  <td>{formatDateTime(p.issued_at)}</td>
                  <td className="mono">
                    <Link to={`/stores/${store.id}/payouts/${p.id}`} title={p.external_id}>{shortExternalId(p.external_id)}</Link>
                  </td>
                  <td><StatusBadge status={p.status} /></td>
                  <td className="num"><Amount minor={p.declared_net_minor} currency={p.currency} /></td>
                  <td className="num"><Amount minor={p.computed_net_minor} currency={p.currency} /></td>
                  <td className="num"><Amount minor={p.delta_minor} currency={p.currency} signed /></td>
                  <td><ResultBadge result={p.result} /></td>
                  <td className="num">{formatCount(p.line_count)}</td>
                  <td className="num">{p.open_findings === "0" ? "—" : <strong>{formatCount(p.open_findings)}</strong>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {payouts.length === PAGE_SIZE ? (
        <p className="hint">Showing the {PAGE_SIZE} most recent payouts.</p>
      ) : null}
    </>
  );
}
